import { useState, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchAuthStatus,
  fetchAutoTradeStatus, toggleAutoTrade,
  fetchAutoBankniftyStatus, toggleAutoBanknifty,
  fetchEmergencyStopStatus, emergencyStop, emergencyStopDisable,
  fetchCrashProtectionStatus, toggleCrashProtection,
} from "@/api/client";
import { useTradingStore } from "@/store/tradingStore";
import { queryKeys } from "@/api/queryKeys";
import { isStateStoreEnabled } from "@/state/store";
import { useKiteToken } from "@/state/hooks/useAuthStatusState";
import { useSystemHealth } from "@/state/hooks/useSystemHealthState";
import {
  AlertTriangle, X, ShieldAlert,
  Settings, User, LogOut, ScrollText, FlaskConical,
  MessageSquare,
} from "lucide-react";

function useIsMarketOpen() {
  const now = new Date();
  const day = now.getDay();
  const hm  = now.getHours() * 100 + now.getMinutes();
  return day >= 1 && day <= 5 && hm >= 915 && hm < 1530;
}
import ConfirmModal from "@/components/shared/ConfirmModal";
import type { Page } from "@/types/trading";

export const PAGE_TITLES: Record<Page, string> = {
  // new consolidated pages
  home:          "Home",
  lab:           "Lab",
  // active pages
  trade:         "Trades",
  pnl:           "P&L",
  paper:         "Paper Trading",
  chat:          "Chat",
  settings:      "Settings",
  metrics:       "Metrics",
  // legacy — kept while old routes still render
  dashboard:     "Dashboard",
  subscriptions: "Subscriptions",
  gtt:           "GTT Scanner",
  replay:        "Replay",
  "day-replay":  "Day Chart",
  "scan-monitor": "Scan Monitor",
  "strategy-pnl": "Strategy P&L",
  backtest:      "Research",
  scheduler:     "Scheduler",
  logs:          "Logs",
};

interface TopBarProps {
  page: Page;
  setPage: (p: Page) => void;
  onMenuClick?: () => void;
  onLogout?: () => void;
}

/* ── Sub-component: TopBarControls ───────────────────────────────────────── */
function TopBarControls({
  autoEnabled, autoToggle, confirmAutoToggle, setConfirmAutoToggle,
  autoBnEnabled, autoBnToggle, confirmBnToggle, setConfirmBnToggle,
}: {
  autoEnabled: boolean; autoToggle: { isPending: boolean; mutate: () => void };
  confirmAutoToggle: boolean; setConfirmAutoToggle: (v: boolean) => void;
  autoBnEnabled: boolean; autoBnToggle: { isPending: boolean; mutate: () => void };
  confirmBnToggle: boolean; setConfirmBnToggle: (v: boolean) => void;
}) {
  return (
    <>
      {/* Auto trade toggle modal */}
      {confirmAutoToggle && (
        <ConfirmModal
          title={autoEnabled ? "Stop equity auto-trading?" : "Start equity auto-trading?"}
          body={autoEnabled
            ? "This will disable automated equity signal execution. Open positions are unaffected — they will continue to be monitored, but no new equity entries will be taken. BankNifty auto is unaffected."
            : "This will enable automated equity trading. Orders will be placed automatically based on scan results. BankNifty auto is controlled separately."
          }
          confirmLabel={autoEnabled ? "Yes, stop auto-trading" : "Yes, start auto-trading"}
          variant={autoEnabled ? "danger" : "warning"}
          loading={autoToggle.isPending}
          onConfirm={() => { autoToggle.mutate(); setConfirmAutoToggle(false); }}
          onCancel={() => setConfirmAutoToggle(false)}
        />
      )}

      {/* BankNifty auto toggle modal */}
      {confirmBnToggle && (
        <ConfirmModal
          title={autoBnEnabled ? "Stop Index auto-trading?" : "Enable Index auto-trading?"}
          body={autoBnEnabled
            ? "This will disable automated index strategies (BankNifty, Nifty, Finnifty). Any active strategy groups will continue to be monitored and closed at their normal exit conditions — no new strategies will be entered."
            : "This will enable automated index strategies (BankNifty, Nifty, Finnifty — credit, directional, momentum). Strategies only execute when VIX < 18 and market conditions qualify. Per-index limits apply."
          }
          confirmLabel={autoBnEnabled ? "Yes, disable Index auto" : "Yes, enable Index auto"}
          variant={autoBnEnabled ? "danger" : "warning"}
          loading={autoBnToggle.isPending}
          onConfirm={() => { autoBnToggle.mutate(); setConfirmBnToggle(false); }}
          onCancel={() => setConfirmBnToggle(false)}
        />
      )}
    </>
  );
}

/* ── Sub-component: TopBarStatus ────────────────────────────────────────── */
function TopBarStatus({
  wsStatus, tokenHours,
}: {
  wsStatus: string; tokenHours: number;
}) {
  return (
    <>
      {/* WebSocket connection status (market-aware) */}
      {(() => {
        const now = new Date();
        const ist = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
        const h = ist.getHours(), m = ist.getMinutes(), wd = ist.getDay();
        const mktOpen = wd >= 1 && wd <= 5 && (h > 9 || (h === 9 && m >= 15)) && (h < 15 || (h === 15 && m <= 30));
        const wsOff = wsStatus !== "live";
        const label = !mktOpen ? "CLOSED" : wsOff ? "OFF" : "LIVE";
        const cls = !mktOpen ? "bg-zinc-700/50 text-zinc-500" : wsOff ? "bg-red-500/15 text-red-400" : "bg-emerald-500/15 text-emerald-400";
        const dot = !mktOpen ? "bg-zinc-500" : wsOff ? "bg-red-400" : "bg-emerald-400";
        return (
          <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1 ${cls}`} title={`WebSocket: ${wsStatus} | Market: ${mktOpen ? "open" : "closed"}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${dot} ${mktOpen && wsStatus === "live" ? "animate-pulse" : ""}`} />
            {label}
          </span>
        );
      })()}
      {/* Token expiry countdown */}
      {tokenHours > 0 && (
        <span
          className={`text-[10px] font-mono px-2 py-0.5 rounded ${
            tokenHours < 2 ? "bg-red-500/20 text-red-400" :
            tokenHours < 6 ? "bg-amber-500/20 text-amber-400" :
            "bg-zinc-800 text-zinc-500"
          }`}
          title={`Kite token expires in ${tokenHours}h`}
        >
          {tokenHours < 1 ? `${Math.round(tokenHours * 60)}m` : `${tokenHours.toFixed(1)}h`}
        </span>
      )}
    </>
  );
}

/* ── Sub-component: EmergencyStopModal ──────────────────────────────────── */
function EmergencyStopModal({
  emergencyPhase, emergencyError, emergencyActivateData, emergencyDisableData,
  emergencyMutation, emergencyDisableMutation, closeEmergencyModal,
  storePositions, autoBnStatus,
}: {
  emergencyPhase: string | null;
  emergencyError: string | null;
  emergencyActivateData: { closed_bn_groups: number; rejected_signals: number; paused_jobs: number } | null;
  emergencyDisableData: { resumed_jobs: number } | null;
  emergencyMutation: { isPending: boolean; mutate: () => void };
  emergencyDisableMutation: { isPending: boolean; mutate: () => void };
  closeEmergencyModal: () => void;
  storePositions: Array<{ symbol: string; quantity: number; unrealised_pnl: number; ltp: number; avg_price: number; instrument_type?: string }>;
  autoBnStatus: { groups?: Array<{ group_id: string; closed: boolean; strategy_name: string; atm_strike: number; expiry: string; max_credit: number; trail_phase: string; net_pnl: number }> } | undefined;
}) {
  /* Emergency stop ACTIVATE confirmation */
  if (emergencyPhase === "confirm-activate") {
    const openPositions = storePositions.filter(p => p.quantity !== 0);
    const bnGroups = autoBnStatus?.groups?.filter(g => !g.closed) ?? [];
    const hasAnything = openPositions.length > 0 || bnGroups.length > 0;

    const totalEquityPnl = openPositions.reduce((s, p) => s + p.unrealised_pnl, 0);
    const totalBnPnl = bnGroups.reduce((s, g) => s + g.net_pnl, 0);
    const grandTotal = totalEquityPnl + totalBnPnl;

    return (
      <ConfirmModal
        title="Emergency Stop — halt all trading?"
        confirmLabel="Yes, stop everything"
        variant="danger"
        size="lg"
        loading={emergencyMutation.isPending}
        onConfirm={() => emergencyMutation.mutate()}
        onCancel={closeEmergencyModal}
        body={
          <div className="space-y-4">
            {emergencyError && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-semibold">
                <AlertTriangle size={12} className="shrink-0" />
                {emergencyError}
              </div>
            )}
            <p className="text-zinc-400">
              This will immediately disable all auto-trading, pause all schedulers and scans, reject pending signals{hasAnything ? ", and square off all positions at market price" : ""}.
            </p>

            {/* Equity / Futures positions */}
            {openPositions.length > 0 && (
              <div>
                <p className="text-zinc-500 text-[11px] uppercase tracking-wide font-semibold mb-1.5">
                  Equity &amp; Futures — {openPositions.length} position{openPositions.length !== 1 ? "s" : ""}
                </p>
                {/* Mobile cards (<sm) */}
                <div className="sm:hidden space-y-2">
                  {openPositions.map(p => {
                    const side = p.quantity > 0 ? "LONG" : "SHORT";
                    const move = p.ltp - p.avg_price;
                    const movePct = p.avg_price > 0 ? (move / p.avg_price) * 100 : 0;
                    return (
                      <div key={p.symbol} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2 flex-wrap min-w-0">
                            <span className="font-mono font-semibold text-zinc-100 text-sm truncate">{p.symbol}</span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${side === "LONG" ? "bg-buy/15 text-buy" : "bg-sell/15 text-sell"}`}>{side}</span>
                          </div>
                          <div className={`font-mono font-bold text-sm shrink-0 ${p.unrealised_pnl >= 0 ? "text-buy" : "text-sell"}`}>
                            {p.unrealised_pnl >= 0 ? "+" : ""}{"\u20B9"}{Math.abs(p.unrealised_pnl).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-[11px]">
                          <div><div className="text-zinc-600 text-[10px] uppercase tracking-wide">Qty</div><div className="font-mono text-zinc-300 font-semibold">{Math.abs(p.quantity)}</div></div>
                          <div><div className="text-zinc-600 text-[10px] uppercase tracking-wide">Avg</div><div className="font-mono text-zinc-400">{"\u20B9"}{p.avg_price.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div></div>
                          <div>
                            <div className="text-zinc-600 text-[10px] uppercase tracking-wide">LTP</div>
                            <div className="font-mono text-zinc-200 font-semibold">
                              {"\u20B9"}{p.ltp.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                              <span className={`ml-1 text-[9px] ${movePct >= 0 ? "text-buy" : "text-sell"}`}>({movePct >= 0 ? "+" : ""}{movePct.toFixed(2)}%)</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {openPositions.length > 1 && (
                    <div className="flex items-center justify-between rounded-xl border border-zinc-700 bg-zinc-900 px-3 py-2 text-xs">
                      <span className="text-zinc-400 font-semibold">Total equity P&amp;L</span>
                      <span className={`font-mono font-bold ${totalEquityPnl >= 0 ? "text-buy" : "text-sell"}`}>
                        {totalEquityPnl >= 0 ? "+" : ""}{"\u20B9"}{Math.abs(totalEquityPnl).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  )}
                </div>
                {/* Desktop table (≥sm) */}
                <div className="hidden sm:block rounded-xl border border-zinc-800 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-zinc-900 border-b border-zinc-800 text-zinc-500">
                        <th className="text-left px-3 py-2 font-semibold">Symbol</th>
                        <th className="text-center px-3 py-2 font-semibold">Side</th>
                        <th className="text-right px-3 py-2 font-semibold">Qty</th>
                        <th className="text-right px-3 py-2 font-semibold">Avg Entry</th>
                        <th className="text-right px-3 py-2 font-semibold">LTP</th>
                        <th className="text-right px-3 py-2 font-semibold">P&amp;L on Close</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openPositions.map(p => {
                        const side = p.quantity > 0 ? "LONG" : "SHORT";
                        const move = p.ltp - p.avg_price;
                        const movePct = p.avg_price > 0 ? (move / p.avg_price) * 100 : 0;
                        return (
                          <tr key={p.symbol} className="border-b border-zinc-800/40 last:border-0 hover:bg-zinc-900/40">
                            <td className="px-3 py-2 text-zinc-100 font-semibold font-mono">{p.symbol}</td>
                            <td className="px-3 py-2 text-center">
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${side === "LONG" ? "bg-buy/15 text-buy" : "bg-sell/15 text-sell"}`}>
                                {side}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right text-zinc-300 font-mono">{Math.abs(p.quantity)}</td>
                            <td className="px-3 py-2 text-right text-zinc-400 font-mono">
                              {"\u20B9"}{p.avg_price.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2 text-right text-zinc-200 font-mono font-semibold">
                              {"\u20B9"}{p.ltp.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                              <span className={`ml-1 text-[10px] ${movePct >= 0 ? "text-buy" : "text-sell"}`}>
                                ({movePct >= 0 ? "+" : ""}{movePct.toFixed(2)}%)
                              </span>
                            </td>
                            <td className={`px-3 py-2 text-right font-mono font-bold ${p.unrealised_pnl >= 0 ? "text-buy" : "text-sell"}`}>
                              {p.unrealised_pnl >= 0 ? "+" : ""}{"\u20B9"}{Math.abs(p.unrealised_pnl).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {openPositions.length > 1 && (
                      <tfoot>
                        <tr className="border-t border-zinc-700 bg-zinc-900">
                          <td colSpan={5} className="px-3 py-2 text-zinc-400 text-xs font-semibold">Total equity P&amp;L</td>
                          <td className={`px-3 py-2 text-right font-mono font-bold text-xs ${totalEquityPnl >= 0 ? "text-buy" : "text-sell"}`}>
                            {totalEquityPnl >= 0 ? "+" : ""}{"\u20B9"}{Math.abs(totalEquityPnl).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            )}

            {/* BankNifty strategy groups */}
            {bnGroups.length > 0 && (
              <div>
                <p className="text-zinc-500 text-[11px] uppercase tracking-wide font-semibold mb-1.5">
                  BankNifty Strategy Groups — {bnGroups.length} active
                </p>
                {/* Mobile cards (<sm) */}
                <div className="sm:hidden space-y-2">
                  {bnGroups.map(g => (
                    <div key={g.group_id} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                          <span className="font-semibold text-zinc-100 text-sm truncate">{g.strategy_name}</span>
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${
                            g.trail_phase === "momentum" ? "bg-buy/15 text-buy"
                            : g.trail_phase === "breakeven" ? "bg-yellow-400/15 text-yellow-400"
                            : "bg-zinc-800 text-zinc-400"
                          }`}>{g.trail_phase}</span>
                        </div>
                        <div className={`font-mono font-bold text-sm shrink-0 ${g.net_pnl >= 0 ? "text-buy" : "text-sell"}`}>
                          {g.net_pnl >= 0 ? "+" : ""}{"\u20B9"}{Math.abs(g.net_pnl).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-[11px]">
                        <div><div className="text-zinc-600 text-[10px] uppercase tracking-wide">ATM</div><div className="font-mono text-zinc-300">{g.atm_strike.toLocaleString("en-IN")}</div></div>
                        <div><div className="text-zinc-600 text-[10px] uppercase tracking-wide">Expiry</div><div className="text-zinc-400">{g.expiry}</div></div>
                        <div><div className="text-zinc-600 text-[10px] uppercase tracking-wide">Max Credit</div><div className="font-mono text-zinc-300">{"\u20B9"}{g.max_credit.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</div></div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Desktop table (≥sm) */}
                <div className="hidden sm:block rounded-xl border border-zinc-800 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-zinc-900 border-b border-zinc-800 text-zinc-500">
                        <th className="text-left px-3 py-2 font-semibold">Strategy</th>
                        <th className="text-right px-3 py-2 font-semibold">ATM Strike</th>
                        <th className="text-right px-3 py-2 font-semibold">Expiry</th>
                        <th className="text-right px-3 py-2 font-semibold">Max Credit</th>
                        <th className="text-right px-3 py-2 font-semibold">Phase</th>
                        <th className="text-right px-3 py-2 font-semibold">P&amp;L on Close</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bnGroups.map(g => (
                        <tr key={g.group_id} className="border-b border-zinc-800/40 last:border-0 hover:bg-zinc-900/40">
                          <td className="px-3 py-2 text-zinc-100 font-semibold">{g.strategy_name}</td>
                          <td className="px-3 py-2 text-right text-zinc-300 font-mono">{g.atm_strike.toLocaleString("en-IN")}</td>
                          <td className="px-3 py-2 text-right text-zinc-400">{g.expiry}</td>
                          <td className="px-3 py-2 text-right text-zinc-300 font-mono">{"\u20B9"}{g.max_credit.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                          <td className="px-3 py-2 text-right">
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${
                              g.trail_phase === "momentum" ? "bg-buy/15 text-buy"
                              : g.trail_phase === "breakeven" ? "bg-yellow-400/15 text-yellow-400"
                              : "bg-zinc-800 text-zinc-400"
                            }`}>
                              {g.trail_phase}
                            </span>
                          </td>
                          <td className={`px-3 py-2 text-right font-mono font-bold ${g.net_pnl >= 0 ? "text-buy" : "text-sell"}`}>
                            {g.net_pnl >= 0 ? "+" : ""}{"\u20B9"}{Math.abs(g.net_pnl).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Grand total */}
            {hasAnything && (
              <div className={`flex items-center justify-between px-4 py-3 rounded-xl border ${grandTotal >= 0 ? "bg-buy/5 border-buy/20" : "bg-sell/5 border-sell/20"}`}>
                <span className="text-zinc-300 text-sm font-semibold">Total realised on close</span>
                <span className={`font-black text-lg font-mono ${grandTotal >= 0 ? "text-buy" : "text-sell"}`}>
                  {grandTotal >= 0 ? "+" : ""}{"\u20B9"}{Math.abs(grandTotal).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                </span>
              </div>
            )}
          </div>
        }
      />
    );
  }

  /* Emergency stop DISABLE confirmation */
  if (emergencyPhase === "confirm-disable") {
    return (
      <ConfirmModal
        title="Disable Emergency Stop — resume trading?"
        confirmLabel="Yes, resume everything"
        variant="warning"
        loading={emergencyDisableMutation.isPending}
        onConfirm={() => emergencyDisableMutation.mutate()}
        onCancel={closeEmergencyModal}
        body={
          <div className="space-y-3">
            {emergencyError && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-semibold">
                <AlertTriangle size={12} className="shrink-0" />
                {emergencyError}
              </div>
            )}
            <p className="text-zinc-400">
              This will re-enable auto-trading, BankNifty strategies, and resume all paused scheduler jobs.
              Scans and trades will begin executing again immediately.
            </p>
          </div>
        }
      />
    );
  }

  /* Emergency stop ACTIVATE result */
  if (emergencyPhase === "result-activate" && emergencyActivateData) {
    return (
      <ConfirmModal
        title="Emergency Stop activated"
        body={
          <div className="space-y-2">
            <p className="text-red-400 font-semibold">All trading halted.</p>
            <p>{emergencyActivateData.paused_jobs > 0 ? `${emergencyActivateData.paused_jobs} scheduler job(s) paused.` : "No scheduler jobs to pause."}</p>
            <p>{emergencyActivateData.closed_bn_groups > 0 ? `${emergencyActivateData.closed_bn_groups} BankNifty group(s) squared off.` : "No active BankNifty groups."}</p>
            <p>All open positions closed at market price.</p>
            {emergencyActivateData.rejected_signals > 0
              ? <p>{emergencyActivateData.rejected_signals} pending signal{emergencyActivateData.rejected_signals !== 1 ? "s" : ""} rejected.</p>
              : <p className="text-zinc-500">No pending signals to reject.</p>
            }
          </div>
        }
        confirmLabel="OK"
        cancelLabel=""
        variant="danger"
        onConfirm={closeEmergencyModal}
        onCancel={closeEmergencyModal}
      />
    );
  }

  /* Emergency stop DISABLE result */
  if (emergencyPhase === "result-disable" && emergencyDisableData) {
    return (
      <ConfirmModal
        title="Emergency Stop disabled"
        body={
          <div className="space-y-2">
            <p className="text-buy font-semibold">Trading resumed.</p>
            <p>Auto-trading and BankNifty re-enabled.</p>
            <p>{emergencyDisableData.resumed_jobs > 0 ? `${emergencyDisableData.resumed_jobs} scheduler job(s) resumed.` : "No scheduler jobs to resume."}</p>
          </div>
        }
        confirmLabel="OK"
        cancelLabel=""
        variant="warning"
        onConfirm={closeEmergencyModal}
        onCancel={closeEmergencyModal}
      />
    );
  }

  return null;
}

export default function TopBar({ page, setPage, onLogout }: TopBarProps) {
  const qc = useQueryClient();
  useIsMarketOpen();
  const [profileOpen, setProfileOpen] = useState(false);
  // Phase B.1: read auth + token from state-store; legacy useQuery only fires
  // when the store is opted out (storeEnabled=false).
  const _storeOnAuth = isStateStoreEnabled();
  const _storeToken  = useKiteToken();
  useQuery({
    queryKey: queryKeys.authStatus,
    queryFn: fetchAuthStatus,
    enabled: !_storeOnAuth,
  });
  const tokenQuery = useQuery({
    queryKey: queryKeys.tokenExpiry,
    queryFn: () => fetch("/api/health/token").then(r => r.json()).catch(() => ({ hours_remaining: 0 })),
    enabled: !_storeOnAuth,
  });
  const tokenHours = (_storeOnAuth && _storeToken)
    ? (_storeToken.hours_remaining ?? 0)
    : (tokenQuery.data?.hours_remaining ?? 0);
  // 2026-04-24: WS app_status is the primary source for these toggle states
  // (pushed every 10s via state-store). REST queries fire ONCE on mount for
  // initial hydration + manually on mutation success — no polling. Removed
  // refetchInterval as part of the no-polling architecture.
  // 2026-04-27 Phase H: gated on _storeOnAuth — system_toggles aggregator
  // domain consolidates all 4 status payloads (auto-trade, BN auto, crash
  // protection, emergency stop) into a single push. queryCacheMirror
  // writes each sub-field to the legacy queryKey so this useQuery finds
  // primed data without firing the REST call.
  const { data: autoStatus, refetch: refetchAutoStatus } = useQuery({
    queryKey: queryKeys.autoTradeStatus,
    queryFn: fetchAutoTradeStatus,
    enabled: !_storeOnAuth,
  });
  const { data: autoBnStatus, refetch: refetchAutoBnStatus } = useQuery({
    queryKey: queryKeys.autoBankniftyStatus,
    queryFn: fetchAutoBankniftyStatus,
    enabled: !_storeOnAuth,
  });
  const autoBnToggle = useMutation({
    mutationFn: toggleAutoBanknifty,
    onSuccess: () => refetchAutoBnStatus(),
  });
  const { data: crashStatus, refetch: refetchCrashStatus } = useQuery({
    queryKey: queryKeys.crashProtectionStatus,
    queryFn: fetchCrashProtectionStatus,
    enabled: !_storeOnAuth,
  });
  const crashToggle = useMutation({
    mutationFn: toggleCrashProtection,
    onSuccess: () => refetchCrashStatus(),
  });
  const scanComplete    = useTradingStore(s => s.scanComplete);
  const systemAlerts    = useTradingStore(s => s.systemAlerts);
  const dismissAlert    = useTradingStore(s => s.dismissSystemAlert);
  const appStatus       = useTradingStore(s => s.appStatus);
  const wsStatus        = useTradingStore(s => s.wsStatus);
  const storePositions  = useTradingStore(s => s.positions);

  // Phase B.1: system health from state-store; legacy poll behind flag.
  const _storeHealth = useSystemHealth();
  const { data: sysHealth } = useQuery({
    queryKey: queryKeys.systemHealth,
    queryFn: () => fetch("/api/health/system").then(r => r.json()).catch(() => null),
    enabled: !_storeOnAuth,
  });
  const healthStatus = (_storeOnAuth && _storeHealth)
    ? _storeHealth.status
    : (sysHealth?.status ?? "healthy");
  // Only show banner during market hours (Mon-Fri 09:00-15:45 IST)
  const now = new Date();
  const ist = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  const h = ist.getHours(), m = ist.getMinutes(), wd = ist.getDay();
  const inMarketWindow = wd >= 1 && wd <= 5 && (h >= 9 && (h < 15 || (h === 15 && m <= 45)));
  // Check if the degraded checks are the expected "off-hours" ones (kite/ws/feed)
  const offHoursChecks = useMemo(() => new Set(["kite", "websocket", "market_feed", "static_ip"]), []);
  const failedChecks = useMemo(() => sysHealth?.checks
    ? Object.entries(sysHealth.checks as Record<string, { ok: boolean }>)
        .filter(([, v]) => !v.ok).map(([k]) => k)
    : [], [sysHealth?.checks]);
  const onlyOffHoursFailed = failedChecks.length > 0 && failedChecks.every(c => offHoursChecks.has(c));
  // Only show health banner during market hours (9:15-15:45 IST weekdays).
  // Outside market hours, WS/feed/kite/static_ip being down is expected — don't alarm the user.
  const healthDegraded = healthStatus !== "healthy" && inMarketWindow && !onlyOffHoursFailed;

  const autoToggle = useMutation({
    mutationFn: toggleAutoTrade,
    onSuccess: () => refetchAutoStatus(),
  });

  const [confirmAutoToggle, setConfirmAutoToggle] = useState(false);
  const [confirmBnToggle, setConfirmBnToggle] = useState(false);

  // 2026-04-27 Phase H: gated on _storeOnAuth — system_toggles domain.
  const { data: emergencyStatus, refetch: refetchEmergencyStatus } = useQuery({
    queryKey: queryKeys.emergencyStopStatus,
    queryFn: fetchEmergencyStopStatus,
    enabled: !_storeOnAuth,
  });
  // WS app_status is primary source; REST is fallback.
  // 2026-04-29 (audit BLOCKER #6): UNDEFINED state defaults to ACTIVE (safe).
  // Until either source confirms otherwise, treat the system as if emergency
  // stop is on. This prevents the brief reload window where the operator
  // sees "all good" before WS arrives. False-positive (briefly shows STOP
  // when actually clear) is preferable to false-negative.
  const emergencyActive = appStatus?.emergency_stop_active ?? emergencyStatus?.active ?? true;
  // Single state machine for emergency modal — prevents race conditions between
  // server state and local UI state during mutations.
  type EmergencyPhase =
    | null                   // no modal open
    | "confirm-activate"     // user wants to turn ON — showing confirmation
    | "confirm-disable"      // user wants to turn OFF — showing confirmation
    | "result-activate"      // activation succeeded — showing result
    | "result-disable";      // disable succeeded — showing result
  const [emergencyPhase, setEmergencyPhase] = useState<EmergencyPhase>(null);
  const [emergencyError, setEmergencyError] = useState<string | null>(null);
  const [emergencyActivateData, setEmergencyActivateData] = useState<{ closed_bn_groups: number; rejected_signals: number; paused_jobs: number } | null>(null);
  const [emergencyDisableData, setEmergencyDisableData] = useState<{ resumed_jobs: number } | null>(null);

  const emergencyMutation = useMutation({
    mutationFn: emergencyStop,
    onSuccess: (data) => {
      setEmergencyError(null);
      setEmergencyActivateData({ closed_bn_groups: data.closed_bn_groups, rejected_signals: data.rejected_signals, paused_jobs: data.paused_jobs });
      setEmergencyPhase("result-activate");
      qc.invalidateQueries();
    },
    onError: (err: Error) => {
      setEmergencyError(err.message || "Emergency stop failed — check server logs.");
    },
  });
  const emergencyDisableMutation = useMutation({
    mutationFn: emergencyStopDisable,
    onSuccess: (data) => {
      setEmergencyError(null);
      setEmergencyDisableData({ resumed_jobs: data.resumed_jobs });
      setEmergencyPhase("result-disable");
      qc.invalidateQueries();
    },
    onError: (err: Error) => {
      setEmergencyError(err.message || "Failed to disable emergency stop — check server logs.");
    },
  });

  const closeEmergencyModal = useCallback(() => {
    setEmergencyPhase(null);
    setEmergencyError(null);
    setEmergencyActivateData(null);
    setEmergencyDisableData(null);
    refetchEmergencyStatus();
  }, [refetchEmergencyStatus]);

  // WS appStatus is primary; REST crashStatus is fallback
  const crashEnabled = appStatus?.crash_protection_enabled ?? crashStatus?.enabled ?? false;
  const crashAuto = crashStatus?.auto_triggered ?? false;
  const crashDrop = crashStatus?.nifty_intraday_drop_pct ?? 0;

  const usedPct = autoStatus?.capital_used_pct ?? scanComplete?.capital_used_pct ?? 0;
  const limitPct = autoStatus?.capital_limit_pct ?? 100;
  // WS scanComplete.pending_count is primary source for pending signals
  const pendingCount = scanComplete?.pending_count ?? autoStatus?.pending_count ?? 0;
  const limitReached = (autoStatus?.capital_limit_reached ?? scanComplete?.capital_limit_reached ?? false) && pendingCount > 0;
  // WS appStatus is primary; REST autoStatus is fallback.
  // 2026-04-29 (audit BLOCKER #6): UNDEFINED defaults to OFF (safe). Showing
  // the toggle as ON before the server confirms could mislead an operator
  // into believing trades are flowing when AUTO_TRADE is actually paused.
  const autoEnabled = appStatus?.auto_trade_enabled ?? autoStatus?.enabled ?? false;
  const autoBnEnabled = appStatus?.bn_auto_enabled ?? autoBnStatus?.enabled ?? false;

  return (
    <>
      {/* System alert banners — auto-dismiss after 20s, click to navigate */}
      {systemAlerts.map(alert => {
        // Determine target page based on alert content
        const target: Page | null =
          alert.message.toLowerCase().includes("emergency") ? "settings" :
          alert.message.toLowerCase().includes("kite") || alert.message.toLowerCase().includes("token") || alert.message.toLowerCase().includes("ml") || alert.message.toLowerCase().includes("model") ? "settings" :
          alert.message.toLowerCase().includes("loss halt") || alert.message.toLowerCase().includes("drawdown") || alert.message.toLowerCase().includes("margin") ? "pnl" :
          alert.message.toLowerCase().includes("signal") || alert.message.toLowerCase().includes("scan") ? "trade" :
          alert.message.toLowerCase().includes("banknifty") || alert.message.toLowerCase().includes("directional") ? "home" :
          null;

        return (
        <div
          key={alert.timestamp}
          onClick={() => { if (target) setPage(target); dismissAlert(alert.timestamp); }}
          className={`flex items-center gap-3 border-b px-5 py-2 shrink-0 cursor-pointer transition hover:opacity-80 ${
            alert.level === "critical"
              ? "bg-red-500/10 border-red-500/20"
              : "bg-yellow-400/10 border-yellow-400/20"
          }`}
        >
          <AlertTriangle
            size={13}
            className={`shrink-0 ${alert.level === "critical" ? "text-red-400" : "text-yellow-400"}`}
          />
          <p className={`text-xs font-semibold flex-1 ${
            alert.level === "critical" ? "text-red-400" : "text-yellow-400"
          }`}>
            {alert.message}
          </p>
          <span className={`text-[11px] shrink-0 ${alert.level === "critical" ? "text-red-500/50" : "text-yellow-500/50"}`}>
            {target ? "Click for details →" : ""}
          </span>
          <button
            aria-label="Dismiss alert"
            onClick={(e) => { e.stopPropagation(); dismissAlert(alert.timestamp); }}
            className={`shrink-0 hover:opacity-70 transition ${
              alert.level === "critical" ? "text-red-400" : "text-yellow-400"
            }`}
          >
            <X size={13} />
          </button>
        </div>
        );
      })}

      {/* System health banner — shows when degraded/unhealthy */}
      {healthDegraded && (
        <div className={`flex items-center gap-3 border-b px-5 py-2 shrink-0 ${
          healthStatus === "unhealthy"
            ? "bg-red-500/10 border-red-500/20"
            : "bg-amber-500/10 border-amber-500/20"
        }`}>
          <AlertTriangle size={13} className={healthStatus === "unhealthy" ? "text-red-400 shrink-0" : "text-amber-400 shrink-0"} />
          <p className={`text-xs font-semibold flex-1 ${healthStatus === "unhealthy" ? "text-red-400" : "text-amber-400"}`}>
            System {healthStatus}: {
              sysHealth?.checks
                ? Object.entries(sysHealth.checks as Record<string, { ok: boolean }>)
                    .filter(([, v]) => !v.ok)
                    .map(([k]) => k)
                    .join(", ") || "check logs"
                : "check logs"
            }
          </p>
          <button aria-label="Go to settings" onClick={() => setPage("settings")} className={`text-xs font-bold hover:underline shrink-0 ${
            healthStatus === "unhealthy" ? "text-red-400" : "text-amber-400"
          }`}>Settings →</button>
        </div>
      )}

      {/* Capital-limit warning banner */}
      {limitReached && (
        <div className="flex items-center gap-3 bg-yellow-400/10 border-b border-yellow-400/20 px-5 py-2 shrink-0">
          <AlertTriangle size={13} className="text-yellow-400 shrink-0" />
          <p className="text-yellow-400 text-xs font-semibold flex-1">
            Capital limit reached ({usedPct.toFixed(1)}% of {limitPct}% deployed) —{" "}
            {(autoStatus?.pending_count ?? scanComplete?.pending_count ?? 0)} signal{(autoStatus?.pending_count ?? 0) !== 1 ? "s" : ""} need your approval
          </p>
          <button
            aria-label="Review pending signals"
            onClick={() => setPage("trade")}
            className="text-yellow-400 text-xs font-bold hover:underline shrink-0"
          >
            Review →
          </button>
        </div>
      )}

      {/* Crash protection auto-trigger banner */}
      {crashEnabled && crashAuto && (
        <div className="flex items-center gap-3 bg-orange-500/10 border-b border-orange-500/20 px-5 py-2 shrink-0">
          <ShieldAlert size={13} className="text-orange-400 shrink-0" />
          <p className="text-orange-400 text-xs font-semibold flex-1">
            Crash Protection AUTO-ACTIVE — Nifty down {Math.abs(crashDrop).toFixed(2)}% intraday.
            Only T1/T2 signals. BUY requires conf≥3 + score≥9. Max 5 positions.
          </p>
          <button
            aria-label="Disable crash protection"
            onClick={() => crashToggle.mutate()}
            className="text-orange-400 text-xs font-bold hover:underline shrink-0"
          >
            Disable →
          </button>
        </div>
      )}

      <header className="h-12 border-b border-zinc-800/50 bg-[#08080d] flex items-center justify-between px-3 sm:px-5 shrink-0">
        {/* Left: MCT Trading branding + nav tabs */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <button aria-label="Go to dashboard" onClick={() => setPage("dashboard")} className="inline-flex items-center hover:opacity-80 transition shrink-0" title="Dashboard">
            <span className="text-buy font-black text-[15px] tracking-tight">MCT</span><span className="text-buy/50 text-[15px] font-medium">Trading</span>
          </button>

          {/* Horizontal nav tabs — desktop */}
          <nav className="hidden md:flex items-center gap-1 ml-4" role="tablist" aria-label="Main navigation">
            {([
              { id: "trade" as Page,   label: "Portfolio" },
              { id: "paper" as Page,   label: "Paper" },
              { id: "pnl" as Page,     label: "P&L" },
              { id: "replay" as Page,  label: "Replay" },
              { id: "lab" as Page,     label: "Lab" },
              { id: "logs" as Page,    label: "Logs" },
            ]).map(tab => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={page === tab.id}
                aria-label={`Navigate to ${tab.label}`}
                onClick={() => setPage(tab.id)}
                className={`px-3 py-1.5 text-[13px] font-medium transition border-b-2 -mb-px ${
                  page === tab.id
                    ? "text-buy border-buy"
                    : "text-zinc-500 border-transparent hover:text-zinc-300"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Right: token countdown + emergency stop + scheduler + chat + settings + avatar */}
        <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
          <TopBarStatus wsStatus={wsStatus} tokenHours={tokenHours} />
          <button
            aria-label="Open chat"
            onClick={() => setPage("chat")}
            className="hidden md:flex p-2.5 rounded-lg text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/60 transition"
            title="Chat"
          >
            <MessageSquare size={19} />
          </button>
          <button
            aria-label="Open settings"
            onClick={() => setPage("settings")}
            className="hidden md:flex p-2.5 rounded-lg text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/60 transition"
            title="Settings"
          >
            <Settings size={19} />
          </button>
          {emergencyActive ? (
            <button
              aria-label="Disable emergency stop"
              onClick={() => setEmergencyPhase("confirm-disable")}
              title="Emergency Stop ON — click to resume"
              className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border border-red-500 bg-red-500/15 text-red-400 text-[10px] sm:text-[11px] font-bold hover:bg-red-500/25 transition shrink-0 animate-pulse"
            >
              ● <span className="hidden sm:inline">STOP ON</span><span className="sm:hidden">ON</span>
            </button>
          ) : (
            <button
              aria-label="Activate emergency stop"
              onClick={() => setEmergencyPhase("confirm-activate")}
              title="Emergency stop — close all, halt everything"
              className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border border-red-800/40 bg-red-500/5 text-red-500 text-[10px] sm:text-[11px] font-bold hover:bg-red-500/10 hover:border-red-500/40 transition shrink-0"
            >
              ● STOP
            </button>
          )}
          <div className="relative">
            <button
              aria-label="Open profile menu"
              onClick={() => setProfileOpen(v => !v)}
              className="w-9 h-9 rounded-full bg-gradient-to-br from-brand/40 to-brand/20 border border-brand/30 flex items-center justify-center ml-1.5 cursor-pointer hover:border-brand/60 transition"
              title="Profile"
            >
              <User size={17} className="text-brand/80" />
            </button>
            {profileOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setProfileOpen(false)} />
                <div className="absolute right-0 top-9 z-50 w-44 rounded-xl bg-[#111118] border border-zinc-800/60 shadow-xl py-1">
                  <button
                    aria-label="View logs"
                    onClick={() => { setProfileOpen(false); setPage("logs"); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-400 hover:text-white hover:bg-zinc-800/60 transition"
                  >
                    <ScrollText size={12} /> Logs
                  </button>
                  <button
                    aria-label="Open performance lab"
                    onClick={() => { setProfileOpen(false); setPage("lab"); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-400 hover:text-white hover:bg-zinc-800/60 transition"
                  >
                    <FlaskConical size={12} /> Performance Lab
                  </button>
                  <div className="border-t border-zinc-800/50 my-1" />
                  <button
                    aria-label="Log out"
                    onClick={() => {
                      setProfileOpen(false);
                      localStorage.removeItem("mct_token");
                      onLogout?.();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 transition"
                  >
                    <LogOut size={12} /> Log Out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <TopBarControls
        autoEnabled={autoEnabled} autoToggle={autoToggle}
        confirmAutoToggle={confirmAutoToggle} setConfirmAutoToggle={setConfirmAutoToggle}
        autoBnEnabled={autoBnEnabled} autoBnToggle={autoBnToggle}
        confirmBnToggle={confirmBnToggle} setConfirmBnToggle={setConfirmBnToggle}
      />

      <EmergencyStopModal
        emergencyPhase={emergencyPhase} emergencyError={emergencyError}
        emergencyActivateData={emergencyActivateData} emergencyDisableData={emergencyDisableData}
        emergencyMutation={emergencyMutation} emergencyDisableMutation={emergencyDisableMutation}
        closeEmergencyModal={closeEmergencyModal}
        storePositions={storePositions} autoBnStatus={autoBnStatus}
      />

    </>
  );
}
